/** Note
 * Minimum amount for GOLD and SILVER is Rs.10.
 */

export const BULLION_PRODUCT = {
    GOLD: {
        TYPE: "GOLD",
        CODE: "XAU",
        PRICE_SYMBOL: "A",
        WEIGHT_SYMBOL: "W",
        ONETIME: {
            MIN_AMT: 10,
            MAX_AMT: 200000,
        },
        SIP: {
            DAILY: {
                MIN_AMT: 10,
                MAX_AMT: 5000,
                LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
            },
            WEEKLY: {
                MIN_AMT: 10,
                MAX_AMT: 5000,
                LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
            },
            MONTHLY: {
                MIN_AMT: 10,
                MAX_AMT: 15000,
                LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
            },
        },
    },
    SILVER: {
        TYPE: "SILVER",
        CODE: "XAG",
        PRICE_SYMBOL: "A",
        WEIGHT_SYMBOL: "W",
        ONETIME: {
            MIN_AMT: 10,
            MAX_AMT: 200000,
        },
        SIP: {
            DAILY: {
                MIN_AMT: 10,
                MAX_AMT: 5000,
                LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
            },
            WEEKLY: {
                MIN_AMT: 10,
                MAX_AMT: 5000,
                LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
            },
            MONTHLY: {
                MIN_AMT: 10,
                MAX_AMT: 15000,
                LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
            },
        },
    },
} as const;

export enum PRODUCT_CURRENCY {
    INR = "₹",
    USD = "$",
    EUR = "€",
}

export enum PRODUCT_WEIGHT {
    GRAM = "gm",
    MILLIGRAM = "mg",
}

export type BULLION_KEY = keyof typeof BULLION_PRODUCT;
export type PRODUCT_KEY = (typeof BULLION_PRODUCT)[BULLION_KEY]["CODE"];
export type CURRENCY_KEY = keyof typeof PRODUCT_CURRENCY;
export type UNIT_KEY = keyof typeof PRODUCT_WEIGHT;

export type T_CURRENCY = {
    get CURRENCY();
    set CURRENCY(val: CURRENCY_KEY);
};

export type T_UNIT = {
    get UNIT();
    set UNIT(val: UNIT_KEY);
};

export type T_CODE = {
    get CODE();
    set CODE(val: PRODUCT_KEY);
};

type T_LAST_FETCHED = {
    get LAST_FETCHED();
    set LAST_FETCHED(val: { AMOUNT: number; WEIGHT: number });
};

export type ITEM_BULLION = {
    [TYPE in BULLION_KEY]: {
        _CURRENCY: CURRENCY_KEY;
        _UNIT: UNIT_KEY;
        _CODE: PRODUCT_KEY;
        _LAST_FETCHED: { AMOUNT: 0; WEIGHT: 0 };
    } & T_CURRENCY &
        T_UNIT &
        T_CODE &
        T_LAST_FETCHED;
};

export type T_BULLION = {
    _TYPE: BULLION_KEY;
    get TYPE(): BULLION_KEY;
    set TYPE(val: BULLION_KEY);
} & ITEM_BULLION;

export const BULLION_PRODUCT_LIST = Object.keys(
    BULLION_PRODUCT,
) as (keyof typeof BULLION_PRODUCT)[];

export const BULLION: T_BULLION = {
    _TYPE: BULLION_PRODUCT.GOLD.TYPE,
    get TYPE() {
        return this._TYPE;
    },
    set TYPE(val) {
        this._TYPE = val;
    },
    GOLD: {
        _CURRENCY: "INR",
        _UNIT: "GRAM",
        _CODE: "XAU",
        _LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
        get CODE() {
            return this._CODE;
        },
        set CODE(val) {
            this._CODE = val;
        },
        get CURRENCY() {
            return this._CURRENCY;
        },
        set CURRENCY(val) {
            this._CURRENCY = val;
        },
        get UNIT() {
            return this._UNIT;
        },
        set UNIT(val) {
            this._UNIT = val;
        },
        get LAST_FETCHED() {
            return this._LAST_FETCHED;
        },
        set LAST_FETCHED(val) {
            this._LAST_FETCHED = val;
        },
    },

    SILVER: {
        _CURRENCY: "INR",
        _UNIT: "GRAM",
        _CODE: "XAG",
        _LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
        get CODE() {
            return this._CODE;
        },
        set CODE(val) {
            this._CODE = val;
        },
        get CURRENCY() {
            return this._CURRENCY;
        },
        set CURRENCY(val) {
            this._CURRENCY = val;
        },
        get UNIT() {
            return this._UNIT;
        },
        set UNIT(val) {
            this._UNIT = val;
        },
        get LAST_FETCHED() {
            return this._LAST_FETCHED;
        },
        set LAST_FETCHED(val) {
            this._LAST_FETCHED = val;
        },
    },
};

export interface PeriodMapping {
    key: string;
    label: string;
}

export const periodMappings: PeriodMapping[] = [
    // { key: "1D", label: "1 D" },
    { key: "1W", label: "1 W" },
    { key: "1M", label: "1 M" },
    { key: "3M", label: "3 M" },
    { key: "6M", label: "6 M" },
    { key: "1Y", label: "1 Y" },
    { key: "3Y", label: "3 Y" },
    { key: "5Y", label: "5 Y" },
];

// export const PERIOD_TYPE = ["1M", "3M", "1Y", "2Y", "3Y", "all"] as const;
export type PeriodType = (typeof periodMappings)[number]["key"];

export enum BullionInvestmentType {
    ONETIME = "ONETIME",
    SIP = "SIP",
}
export type BULLION_INVESTMENT_TYPE_KEY = keyof typeof BullionInvestmentType;

export enum BullionSIPType {
    DAILY = "DAILY",
    WEEKLY = "WEEKLY",
    MONTHLY = "MONTHLY",
}
export type BULLION_SIP_TYPE_KEY = keyof typeof BullionSIPType;
export interface TradeData {
    buy_pretax: number;
    date_time: string; // ISO 8601 formatted date string
}
export interface LatestDetails {
    amount: number;
    grams: number;
    tax_rate: number;
    taxed_amount: number;
    pre_tax_amount: number;
    amount_per_gram: number;
}
export interface ProductData {
    prices: TradeData[];
    latest_details: LatestDetails;
    live_price: number;
}

export interface ProductDataResponse {
    data: ProductData;
    success: boolean;
    message?: string;
    error?: string;
}
